import { useEffect, useMemo, useState } from 'react';

import TermAndCondition from './TermAndCondition';
import UserDetails from './UserDetails';
import useAuthStore from 'store/auth';
import { MILESTONES, WALKTHROUGH_CHECK } from 'lib/constants';
import Banner from './Banner';
import Recommendations from './Recommendations';
import Loader from 'components/Loader';
import UnapprovedUserOverlay from './overlays/UnapprovedUser';
import NoScanOverlay from './overlays/NoScan';
import SingleScan from './SingleScan';
import WelcomeProfile from './WelcomeProfile';
import useTourStore from 'store/app-tour';
import useWalkthrough from 'hooks/useWalkthrough';
import Confetti from 'components/Confetti';
import AppFeatureLinks from './AppFeatureLinks';
import useGetFeatureStatus from 'hooks/useGetFeatureStatus';
import { ScrollArea } from 'components/ui/scroll-area';
import UserProfile from 'components/UserProfile';
import ProfileDetails from 'components/ProfileDetail';
import ScanChart from './ScanChart';
import DraggableVitals from './DraggableVitals';
import { isFeatureEnabled } from 'lib/feature-flags';

const COOKIE_NAME = WALKTHROUGH_CHECK.SIDEMENU;

export default function DashboardPage() {
  // store state
  const currentMilestone = useAuthStore((store) => store.currentMilestone);
  const accountStatus = useAuthStore((store) => store.accountStatus);
  const userAttributes = useAuthStore((store) => store.userAttributes);
  const profileId = useAuthStore((store) => store.adminProfileId);

  const setStartSideMenu = useTourStore((store) => store.setStartSideMenu);

  // hooks state
  const { checkWalk } = useWalkthrough();
  const {
    isLoading,
    isAIReportUpdated,
    isQuestionnaireUpdated,
    isLabReportUpdated,
    checkConfetti,
    showConfetti,
    scanResults,
  } = useGetFeatureStatus();

  // local state
  const [noScan, setNoScan] = useState<boolean>(false);
  const [selectedBar, setSelectedBar] = useState<number>(0);
  const [isApproved, setIsApproved] = useState<boolean>(false);

  useEffect(() => {
    if (accountStatus?.approved) setIsApproved(accountStatus?.approved);
  }, [accountStatus]);

  // checking and showing the sidemenu walkthrough
  useEffect(() => {
    if (profileId) {
      if (
        currentMilestone &&
        [MILESTONES.TNC, MILESTONES.USER_DETAIL].includes(
          currentMilestone as MILESTONES
        )
      ) {
        setStartSideMenu(false);
      } else {
        const hasWalked = checkWalk(COOKIE_NAME);
        if (isApproved) setStartSideMenu(!hasWalked);
      }
    }
  }, [currentMilestone, isApproved, profileId]);

  useEffect(() => {
    if (scanResults?.length) {
      setNoScan(false);
    } else {
      setNoScan(true);
    }
  }, [scanResults?.length]);

  useEffect(() => {
    if (userAttributes && Object.keys(userAttributes)?.length) {
      checkConfetti();
    }
  }, [userAttributes]);

  const selectedBarId = useMemo(
    () => (scanResults?.length ? scanResults[selectedBar].reading_id : ''),
    [selectedBar, scanResults?.length]
  );

  const showAIReport = isFeatureEnabled('AI_REPORT');
  const showLabReport = isFeatureEnabled('LAB_REPORT');
  const showRecommendations = showAIReport || showLabReport;

  if (currentMilestone === MILESTONES.TNC) return <TermAndCondition />;

  if (currentMilestone === MILESTONES.USER_DETAIL) return <UserDetails />;

  if (isLoading) {
    return <Loader className='h-screen' />;
  }

  return (
    <>
      <Confetti
        show={showConfetti}
        className='h-[calc(100vh-1rem)] w-[calc(100vw-5rem)]'
      />

      <div className='grid w-full col-span-1 overflow-hidden lg:grid-cols-9'>
        <div className='relative flex flex-col gap-4 p-4 overflow-hidden lg:h-screen lg:col-span-7'>
          {!isApproved ? <UnapprovedUserOverlay /> : null}

          <div className='shrink-0 min-h-fit lg:h-[22%] 3xl:h-[24%]'>
            <Banner isApproved={isApproved} />
          </div>

          <div className='shrink-0 lg:h-[16%] 3xl:h-[14%]'>
            <AppFeatureLinks />
          </div>

          {isFeatureEnabled('FACE_SCAN') ? (
            isApproved && noScan ? (
              <NoScanOverlay />
            ) : (
              <div className='relative grid flex-1 min-h-0 items-stretch grid-cols-1 sm:grid-cols-12 gap-y-3 sm:gap-x-4 lg:max-h-[42%]'>
                {scanResults?.length ? (
                  scanResults?.length === 1 ? (
                    <SingleScan
                      score={+scanResults?.[0]?.medsi_wellness_score}
                    />
                  ) : (
                    <ScanChart
                      selectedIndex={selectedBar}
                      setSelectedIndex={setSelectedBar}
                      scans={scanResults}
                    />
                  )
                ) : null}

                <div className='w-full h-full min-h-44 col-span-6 overflow-x-scroll lg:col-span-7 hide-scrollbar'>
                  <DraggableVitals
                    scanResults={scanResults}
                    selectedReadingId={selectedBarId}
                  />
                </div>
              </div>
            )
          ) : null}

          {showRecommendations ? (
            <div className='grid shrink-0 grid-cols-1 gap-4 sm:grid-cols-5 lg:grid-cols-1'>
              <div className='grid h-full grid-cols-1 col-span-3 gap-4 lg:col-span-1 lg:grid-cols-2'>
                {showAIReport ? (
                  <Recommendations
                    recommendValue='AI_REPORT'
                    showOverlay={isApproved && !isAIReportUpdated}
                  />
                ) : null}
                {showLabReport ? (
                  <Recommendations
                    recommendValue='LAB_REPORT'
                    showOverlay={isApproved && !isLabReportUpdated}
                  />
                ) : null}
              </div>

              <div className='col-span-2 px-4 pt-2 pb-6 bg-primary-dark lg:hidden'>
                <WelcomeProfile
                  className='h-full'
                  isQuestionnaireUpdated={isQuestionnaireUpdated}
                />
              </div>
            </div>
          ) : (
            <div className='px-4 pt-2 pb-6 bg-primary-dark lg:hidden'>
              <WelcomeProfile
                className='h-full'
                isQuestionnaireUpdated={isQuestionnaireUpdated}
              />
            </div>
          )}
        </div>

        <ScrollArea className='row-span-2 hidden lg:block col-span-2 bg-[#0E2439] p-4 h-screen'>
          <UserProfile>
            <div className='flex flex-col h-full mt-10'>
              <ProfileDetails />
              <div className='flex-1 h-full'>
                <WelcomeProfile
                  isQuestionnaireUpdated={isQuestionnaireUpdated}
                />
              </div>
            </div>
          </UserProfile>
        </ScrollArea>
      </div>
    </>
  );
}
